import type { ReactNode } from 'react';
import type { ModuleCode } from '@retail-ops/shared-types/enums';
import { MODULE_BY_CODE } from '@/constants/modules';
import { useConfig } from '@/contexts/ConfigContext';
import { Pill } from '@/components/primitives';
import { formatLocalTime } from '@/utils/time';

/**
 * Shared landing page used by every module stub.
 *
 * Renders:
 *   - Module title + scope sentence (from Bible §4 / digest §{code}).
 *   - "Coming soon" placeholder.
 *   - Diagnostic panel showing ConfigContext depth so the shell is verifiable
 *     before any module work begins.
 */
export interface ModuleLandingProps {
  code: ModuleCode;
  /** Optional extra content inserted between the scope and the diagnostic panel. */
  extra?: ReactNode;
  /** Module-specific config dependency callouts shown in the diag grid. */
  configDiagnostics?: Array<{ label: string; value: ReactNode }>;
}

export function ModuleLanding({ code, extra, configDiagnostics }: ModuleLandingProps): JSX.Element {
  const def = MODULE_BY_CODE[code];
  const { config, state, error, lastLoadedAt } = useConfig();

  const defaultDiag: Array<{ label: string; value: ReactNode }> = [
    { label: 'Config state', value: state },
    { label: 'Last load', value: lastLoadedAt ? formatLocalTime(lastLoadedAt) : '—' },
    { label: 'Products loaded', value: config.products.length },
    { label: 'Regions loaded', value: config.regions.length },
    { label: 'Disclosures loaded', value: config.disclosures.length },
  ];

  const diag = configDiagnostics ?? defaultDiag;

  return (
    <div className="module-landing">
      <header className="module-landing__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-6)' }}>
          <h1>{def.code} — {def.name}</h1>
          <Pill variant="muted">Module {def.code}</Pill>
          {state === 'initializing' && <Pill variant="warn">System initializing</Pill>}
          {state === 'error' && <Pill variant="err">Config error</Pill>}
        </div>
        <p className="module-landing__scope">{def.scope}</p>
      </header>

      <div className="module-landing__placeholder">
        <strong>Coming soon</strong>
        <div>The {def.code} surface is scaffolded. UI shipping in a following wave.</div>
      </div>

      {extra}

      <div className="module-landing__diag">
        <h3>Diagnostics · Config dependencies</h3>
        {error && (
          <div
            style={{
              padding: 'var(--sp-5)',
              background: 'var(--err-bg)',
              color: 'var(--err-text)',
              borderRadius: 'var(--r-md)',
              marginBottom: 'var(--sp-6)',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}
        <div className="module-landing__diag-grid">
          {diag.map((d) => (
            <div key={d.label} className="module-landing__diag-cell">
              <div className="module-landing__diag-cell-label">{d.label}</div>
              <div className="module-landing__diag-cell-value">{d.value ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
